import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { Customer } from './core/enums/customer.enum';
import { MaintenanceService } from './shared/services/maintenance.service';

interface Config {
    apiUrl: string;
    apiV3Url: string;
    customer?: Customer;
    twoFactorEnabled?: boolean | string;
    maintenance?: boolean | string;
    languageSwitcher?: boolean;
}

export interface IAppConfig {
    apiUrl: string;
    apiV3Url: string;
    bffUrl: string;
    load: () => Promise<void>;
}

@Injectable({
    providedIn: 'root',
})
export class AppConfig implements IAppConfig {
    public apiUrl: string;
    public apiV3Url: string;
    public bffUrl: string;
    public customer?: Customer;
    public twoFactorEnabled?: boolean;
    public maintenance: boolean;
    public languageSwitcher?: boolean = false;

    constructor(
        private readonly http: HttpClient,
        private maintenanceService: MaintenanceService
    ) {}

    public async load(): Promise<void> {
        const config = await lastValueFrom(
            this.http.get<Config>(
                `assets/config.json?v=${new Date().getTime()}`
            )
        );

        if (config.apiUrl === '${API_URL}') {
            config.apiUrl = environment.apiUrl;
        }
        this.apiUrl = config.apiUrl;

        if (config.apiV3Url === '${API_URL_V3}') {
            config.apiV3Url = environment.apiV3Url;
        }
        this.apiV3Url = config.apiV3Url;

        // Derive BFF URL from API URL (same origin, different path)
        const apiOrigin = new URL(this.apiUrl).origin;
        this.bffUrl = `${apiOrigin}/bff/api/v1`;

        if (config.twoFactorEnabled === '${TWO_FACTOR_ENABLED}') {
            config.twoFactorEnabled = environment.twoFactorEnabled;
        } else {
            config.twoFactorEnabled = config.twoFactorEnabled == 'true';
        }
        this.twoFactorEnabled = config.twoFactorEnabled;

        if (config.languageSwitcher) {
            config.languageSwitcher = environment.languageSwitcher;
        }
        this.languageSwitcher = config.languageSwitcher;

        if (config.maintenance === '${MAINTENANCE}') {
            config.maintenance = environment.maintenance;
        } else {
            config.maintenance = config.maintenance == 'true';
        }
        this.maintenance = config.maintenance;

        // Initialize the maintenance service with the current state
        this.maintenanceService.setMaintenanceMode(this.maintenance);

        // Session loading is now handled by AuthGuard on each route
        // This ensures /me endpoint is called on every page load when session is empty
        console.log(
            'App initialized. Authentication will be handled by route guards.'
        );
    }
}

export function initConfig(config: AppConfig): () => Promise<void> {
    return () => config.load();
}
